<template>
  <div class="modal-overlay" @click="closeModal">
    <div class="modal-container" @click.stop>
      <!-- Modal Header -->
      <div class="modal-header">
        <h3 class="modal-title">Historial de: {{ employeeName }}</h3>
        <button class="close-button" @click="closeModal">
          <div class="close-icon"></div>
        </button>
      </div>

      <!-- Modal Content -->
      <div class="modal-content">
        <!-- Table Headers -->
        <div class="table-headers">
          <div class="header-cell email-header">DIRECCIÓN DE CORREO</div>
          <div class="header-cell date-header">FECHA</div>
          <div class="header-cell time-header">HORA</div>
          <div class="header-cell type-header">TIPO DE ENVÍO</div>
          <div class="header-cell status-header">ESTADO DE ENVÍO</div>
          <div class="header-cell spam-header">REPORTE SPAM</div>
        </div>

        <!-- Scrollable Table Rows -->
        <div class="table-rows-container">
          <div class="table-rows">
            <div v-for="(email, index) in filteredEmailHistory" :key="index" class="table-row">
              <div class="data-cell email-cell">{{ email.email }}</div>
              <div class="data-cell date-cell">{{ formatDate(email.sentDate) }}</div>
              <div class="data-cell time-cell">{{ formatTime(email.sentDate) }}</div>
              <div class="data-cell type-cell">
                <div class="email-type-badge" :class="getEmailTypeClass(email.emailType)">
                  {{ getEmailTypeLabel(email.emailType) }}
                </div>
              </div>
              <div class="data-cell status-cell">
                <span :class="getDeliveryStatusClass(email.deliveryStatus)">
                  {{ email.deliveryStatus }}
                </span>
              </div>
              <div class="data-cell spam-cell">
                <span :class="getSpamReportClass(email.deliveryStatus)">
                  {{ getSpamReportText(email.deliveryStatus) }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { DeliveryStatus, EMAIL_TYPES, EmailHistoryDTO } from 'commons'
import { deliveryStatusClassFromStatus } from '../../utils/delivery-status-utils'

interface Props {
  isVisible: boolean
  employeeName: string
  emailHistory: EmailHistoryDTO[]
  hideSent: boolean
}

const props = defineProps<Props>()
const emit = defineEmits<{
  close: []
  toggleHideSent: []
}>()

const closeModal = () => {
  emit('close')
}

const filteredEmailHistory = computed(() => {
  if (props.hideSent) {
    return props.emailHistory.filter((email) => email.deliveryStatus !== 'SENT TO SES')
  }
  return props.emailHistory
})

const formatDate = (dateString: string | Date) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('es-MX', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  })
}

const formatTime = (dateString: string | Date) => {
  const date = new Date(dateString)
  return date.toLocaleTimeString('es-MX', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  })
}

const getEmailTypeLabel = (emailType: string) => {
  switch (emailType) {
    case EMAIL_TYPES.START_REGISTRATION.type:
      return EMAIL_TYPES.START_REGISTRATION.type_to_show
    case EMAIL_TYPES.PENDING_REGISTRATION.type:
      return EMAIL_TYPES.PENDING_REGISTRATION.type_to_show
    case EMAIL_TYPES.INVITATION_REGISTRATION.type:
      return EMAIL_TYPES.INVITATION_REGISTRATION.type_to_show
    case EMAIL_TYPES.UPDATED_EMAIL_NOTIFICATION.type:
      return EMAIL_TYPES.UPDATED_EMAIL_NOTIFICATION.type_to_show
    default:
      return emailType
  }
}

const getEmailTypeClass = (emailType: string) => {
  switch (emailType) {
    case EMAIL_TYPES.START_REGISTRATION.type:
      return 'type-reminder'
    case EMAIL_TYPES.PENDING_REGISTRATION.type:
      return 'type-continue'
    case EMAIL_TYPES.INVITATION_REGISTRATION.type:
      return 'type-creation'
    case EMAIL_TYPES.UPDATED_EMAIL_NOTIFICATION.type:
      return 'type-updated-email'
    default:
      return 'type-default'
  }
}

const getDeliveryStatusClass = (status: string) => {
  return deliveryStatusClassFromStatus(status as DeliveryStatus)
}

const getSpamReportText = (deliveryStatus: string) => {
  if (deliveryStatus === DeliveryStatus.SPAM) return 'SI'
  if (
    [DeliveryStatus.ABIERTO, DeliveryStatus.RECIBIDO, DeliveryStatus.ENVIANDO].includes(
      deliveryStatus as DeliveryStatus,
    )
  )
    return 'NO'
  return 'N/A'
}

const getSpamReportClass = (deliveryStatus: string) => {
  const text = getSpamReportText(deliveryStatus)
  if (text === 'SI') return 'spam-yes'
  if (text === 'NO') return 'spam-no'
  return 'spam-na'
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-container {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  padding: 0px 0px 24px;
  gap: 26px;
  position: relative;
  width: 1219px;
  max-height: 80vh;
  background: #ffffff;
  border-radius: 16px;
  overflow: hidden;
}

.modal-header {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 24px 40px;
  gap: 205px;
  width: 1219px;
  height: 80px;
  background: #ffffff;
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.15);
  border-radius: 16px 16px 0 0;
  flex-shrink: 0;
}

.modal-title {
  width: 358px;
  height: 25px;
  font-family: 'Montserrat', sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 18px;
  line-height: 140%;
  color: #4f4f4f;
  margin: 0;
  white-space: nowrap;
}

.close-button {
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0px;
  gap: 15px;
  width: 32px;
  height: 32px;
  border-radius: 50px;
  border: none;
  background: none;
  cursor: pointer;
}

.close-icon {
  width: 32px;
  height: 32px;
  background: #c5c5c5;
  border-radius: 50%;
  position: relative;
}

.close-icon::before,
.close-icon::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 12px;
  height: 2px;
  background: #ffffff;
  transform: translate(-50%, -50%) rotate(45deg);
}

.close-icon::after {
  transform: translate(-50%, -50%) rotate(-45deg);
}

.modal-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px 32px;
  width: 1219px;
  flex: 1;
  overflow: hidden;
}

.table-headers {
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0px;
  width: 1155px;
  height: 60px;
  border: 1px solid #d5d5d5;
  border-bottom: none;
  flex-shrink: 0;
}

.header-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 60px;
  background: #003896;
  color: #fafafa;
  font-family: 'Montserrat', sans-serif;
  font-style: normal;
  font-weight: 700;
  font-size: 14px;
  line-height: 130%;
  text-align: center;
  border-right: 1px solid #d5d5d5;
}

.email-header {
  width: 355px;
}
.date-header {
  width: 120px;
}
.time-header {
  width: 130px;
}
.type-header {
  width: 220px;
}
.status-header {
  width: 180px;
}
.spam-header {
  width: 150px;
  border-right: none;
}

.table-rows-container {
  width: 1155px;
  border: 1px solid #d5d5d5;
  border-top: none;
  max-height: calc(80vh - 200px);
  overflow-y: auto;
  overflow-x: hidden;
}

.table-rows {
  width: 100%;
}

.table-row {
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  padding: 0px;
  width: 100%;
  height: 50px;
  border-bottom: 1px solid #c5c5c5;
}

.table-row:last-child {
  border-bottom: none;
}

.data-cell {
  display: flex;
  align-items: center;
  height: 50px;
  font-family: 'Montserrat', sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 140%;
  color: #2b2a2a;
  border-right: 1px solid #c5c5c5;
  padding: 0 24px;
  white-space: nowrap;
}

.email-cell {
  width: 355px;
}
.date-cell {
  width: 120px;
  justify-content: center;
}
.time-cell {
  width: 130px;
  justify-content: center;
}
.type-cell {
  width: 220px;
  justify-content: center;
}
.status-cell {
  width: 180px;
  justify-content: center;
}
.spam-cell {
  width: 150px;
  justify-content: center;
  border-right: none;
}

.email-type-badge {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 4px;
  gap: 4px;
  border-radius: 24px;
  font-family: 'Montserrat', sans-serif;
  font-style: normal;
  font-weight: 700;
  font-size: 13px;
  line-height: 130%;
  text-align: center;
}

.type-continue {
  background: #aed491;
  border: 1px solid #348127;
  color: #3f7623;
}

.type-reminder {
  background: #d791ff;
  border: 1px solid #b761d6;
  color: #5c00a7;
}

.type-creation {
  background: #91c9ff;
  border: 1px solid #76aeff;
  color: #003ea7;
}

.type-updated-email {
  background: #ffdd91;
  border: 1px solid #ffb347;
  color: #a75c00;
}

.type-default {
  background: #e0e0e0;
  border: 1px solid #bdbdbd;
  color: #666666;
}

.spam-yes {
  color: #d0342c;
  font-weight: 700;
}

.spam-no {
  color: #4eab1f;
  font-weight: 700;
}

.spam-na {
  color: #4f4f4f;
  font-weight: 700;
}

/* Custom scrollbar styling */
.table-rows-container::-webkit-scrollbar {
  width: 8px;
}

.table-rows-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.table-rows-container::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.table-rows-container::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>
